import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import { Link } from 'react-router-dom';

const HealthcarePage = () => {
  const capabilities = [
    {
      title: 'Hospital Operations Optimization',
      description: 'Workflow analysis, capacity planning, and operational efficiency improvements for healthcare facilities.',
      icon: 'ri-hospital-line',
    },
    {
      title: 'Accreditation & Quality Readiness',
      description: 'Preparation for JCI, NABH, and other international healthcare accreditation standards.',
      icon: 'ri-award-line',
    },
    {
      title: 'Healthcare Supply Chain Redesign',
      description: 'Inventory optimization, vendor management, and procurement process improvements.',
      icon: 'ri-links-line',
    },
    {
      title: 'Digital Health Transformation',
      description: 'EMR/EHR implementation, telemedicine integration, and healthcare technology adoption.',
      icon: 'ri-heart-pulse-line',
    },
    {
      title: 'Policy, Governance & Compliance',
      description: 'Healthcare policy development, regulatory compliance, and governance frameworks.',
      icon: 'ri-file-list-3-line',
    },
  ];

  return (
    <div className="min-h-screen bg-black">
      <Navigation />
      
      <main className="pt-[90px]">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-b from-[#0A0A0A] to-black py-32">
          <div className="max-w-[1400px] mx-auto px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-flex items-center px-4 py-2 border border-copper/30 rounded-full mb-8">
                  <span className="text-xs uppercase tracking-wider text-copper">Healthcare Consulting</span>
                </div>
                <h1 className="text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight">
                  Healthcare Operations &{' '}
                  <span className="bg-copper-gradient bg-clip-text text-transparent">
                    Systems Consulting
                  </span>
                </h1>
                <p className="text-xl text-gray-400 leading-relaxed mb-10">
                  Strategic consulting for healthcare providers seeking operational excellence, accreditation readiness, supply chain optimization, and digital health transformation.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-copper hover:bg-copper-light text-white px-10 py-4 rounded-full text-base font-semibold transition-all duration-300 whitespace-nowrap"
                >
                  Schedule Consultation
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="relative h-[500px] rounded-2xl overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/3acea43b1966e4f7e7200a984d5c79b5.jpg"
                  alt="Healthcare Consulting"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Capabilities Grid */}
        <section className="bg-charcoal-dark py-24">
          <div className="max-w-[1400px] mx-auto px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">Our Healthcare Capabilities</h2>
              <p className="text-lg text-gray-400 max-w-3xl mx-auto">
                Comprehensive healthcare consulting services designed to improve patient outcomes, operational efficiency, and regulatory compliance.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {capabilities.map((capability, index) => (
                <div
                  key={index}
                  className="bg-charcoal border border-charcoal-light rounded-2xl p-8 hover:border-copper transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-copper/10 rounded-full flex items-center justify-center mb-6">
                    <i className={`${capability.icon} text-4xl text-copper`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">{capability.title}</h3>
                  <p className="text-base text-gray-400 leading-relaxed">{capability.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Expertise Section */}
        <section className="bg-black py-24">
          <div className="max-w-[1400px] mx-auto px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl font-bold text-white mb-6">Healthcare Expertise</h2>
                <p className="text-lg text-gray-400 leading-relaxed mb-6">
                  Led by Dr. Safwan A. Chaudry through North American Healthcare Group Corp., our healthcare division brings deep clinical and operational expertise to every engagement. We understand the unique challenges facing healthcare providers and deliver practical solutions that improve patient care while optimizing operations.
                </p>
                <ul className="space-y-4">
                  {[
                    'Hospital and clinic operational assessments',
                    'International accreditation preparation and support',
                    'Healthcare supply chain and procurement optimization',
                    'Digital health strategy and EMR/EHR implementation',
                    'Clinical governance and quality improvement programs',
                    'Healthcare policy development and regulatory compliance',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <i className="ri-check-line text-copper text-xl mt-1"></i>
                      <span className="text-base text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative h-[400px] rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=healthcare%20professionals%20reviewing%20patient%20data%20and%20medical%20charts%20in%20modern%20hospital%20setting%2C%20clinical%20team%20collaboration%20with%20technology%20integration%2C%20professional%20medical%20consultation%20with%20sophisticated%20atmosphere&width=700&height=400&seq=health-expertise-002&orientation=landscape"
                  alt="Healthcare Expertise"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-charcoal-dark py-24">
          <div className="max-w-[1400px] mx-auto px-8 text-center">
            <h2 className="text-5xl font-bold text-white mb-6">
              Transform Your Healthcare Operations
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
              Partner with MACGOC to achieve operational excellence and deliver superior patient care.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-copper hover:bg-copper-light text-white px-12 py-4 rounded-full text-lg font-semibold transition-all duration-300 whitespace-nowrap"
            >
              Get Started
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HealthcarePage;